import { cn } from "@/lib/utils";
import DOMPurify from "dompurify";
import { useMemo } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

interface MarkdownRendererProps {
    content: string;
    autolink?: boolean;
    /** Traite les sauts de ligne simples comme des <br> (ancien comportement pré-CommonMark) */
    softBreaks?: boolean;
    className?: string;
}

const DOMAIN_WHITELIST = [
    "https://walter-learning.com",
    "https://*.walter-learning.com",
    "https://walter-apprenticeship.s3.amazonaws.com",
    "https://walter-resources.s3.amazonaws.com",
    "https://walter-strapi.s3.eu-west-3.amazonaws.com"
];

// Construit des expressions régulières à partir des patterns de domaine (supporte "*.domaine.com")
const buildWhitelistRegex = (pattern: string): RegExp => {
    if (!pattern.startsWith("https://")) {
        return /^$/; // ne rien matcher si protocole inattendu
    }

    const withoutProtocol = pattern.replace(/^https:\/\//, "");

    if (withoutProtocol.startsWith("*.")) {
        const hostSuffix = withoutProtocol.slice(2).replace(/\./g, "\\.");
        return new RegExp(`^https:\\/\\/(?:[\\w-]+\\.)+${hostSuffix}(?::\\d+)?(?:[\\/#?].*)?$`, "i");
    }

    const exactHost = withoutProtocol.replace(/\./g, "\\.");
    return new RegExp(`^https:\\/\\/${exactHost}(?::\\d+)?(?:[\\/#?].*)?$`, "i");
};

const DOMAIN_WHITELIST_REGEXES = DOMAIN_WHITELIST.map(buildWhitelistRegex);

// Auto-lien uniquement pour les URLs whitelistees, en évitant <a> et <code>
const autolinkWhitelisted = (html: string): string => {
    const tokens = html.split(/(<[^>]+>)/g);
    let inCode = false;
    let inAnchor = false;

    const urlRegex = /(https:\/\/[^\s<>()]+?)(?=([.,!?;:]?(?:\s|$)))/g;

    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token.startsWith("<")) {
            if (/^<code\b/i.test(token)) inCode = true;
            if (/^<\/code>/i.test(token)) inCode = false;
            if (/^<a\b/i.test(token)) inAnchor = true;
            if (/^<\/a>/i.test(token)) inAnchor = false;
            continue;
        }

        if (!inCode && !inAnchor && token) {
            tokens[i] = token.replace(urlRegex, match => {
                const isAllowed = DOMAIN_WHITELIST_REGEXES.some(rx => rx.test(match));
                if (!isAllowed) return match;
                return `<a href="${match}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${match}</a>`;
            });
        }
    }

    return tokens.join("");
};

// Fonction pour convertir le markdown basique en HTML de manière sécurisée
const markdownToHtml = (markdown: string, autolink: boolean = true): string => {
    if (!markdown) return "";

    // Échapper seulement les balises script et autres éléments dangereux AVANT la conversion
    const escapeHtml = (text: string) => {
        return text
            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "&lt;script&gt;...&lt;/script&gt;")
            .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, "&lt;object&gt;...&lt;/object&gt;")
            .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, "&lt;embed&gt;...&lt;/embed&gt;")
            .replace(/<form\b[^<]*(?:(?!<\/form>)<[^<]*)*<\/form>/gi, "&lt;form&gt;...&lt;/form&gt;")
            .replace(/on\w+\s*=/gi, "data-removed-event="); // Supprimer les event handlers
    };

    let html = escapeHtml(markdown);

    // Convertir le markdown en HTML de manière sécurisée
    html = html
        // Titres
        .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mb-1">$1</h3>')
        .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mb-1">$1</h2>')
        .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mb-1">$1</h1>')

        // Images (sécurisées - seulement http/https)
        .replace(/!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]+)")?\)/g, (_match, alt, url, title) => {
            if (url.startsWith("http://") || url.startsWith("https://")) {
                const safeAlt = alt ?? "";
                const titleAttr = title ? ` title="${title}"` : "";
                return `<img src="${url}" alt="${safeAlt}"${titleAttr} loading="lazy" decoding="async" class="block mx-auto max-w-full h-auto max-h-[1000px]" />`;
            }
            return alt || "";
        })

        // Gras et italique
        .replace(/(^|[^\w])\*\*(.+?)\*\*([^\w]|$)/g, '$1<strong class="font-semibold">$2</strong>$3')
        .replace(/(^|[^\w])\*(.+?)\*([^\w]|$)/g, '$1<em class="italic">$2</em>$3')
        .replace(/(^|[^\w])__(.+?)__([^\w]|$)/g, '$1<strong class="font-semibold">$2</strong>$3')
        .replace(/(^|[^\w])_(.+?)_([^\w]|$)/g, '$1<em class="italic">$2</em>$3')

        // Code inline
        .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-[80%] font-mono">$1</code>')

        // Liens (sécurisés - seulement http/https)
        .replace(/(?<!!)\[([^\]]+)\]\(([^)]+)\)/g, (_match, text, url) => {
            if (url.startsWith("http://") || url.startsWith("https://")) {
                return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${text}</a>`;
            }
            return text;
        })

        // Listes
        .replace(/^\* (.*$)/gim, '<li class="ml-4">$1</li>')
        .replace(/^- (.*$)/gim, '<li class="ml-4">$1</li>')
        .replace(/^(\d+)\. (.*$)/gim, '<li class="ml-4">$2</li>');

    // Paragraphes
    html = html.replace(/\n\n/g, '</p><p class="mb-1">').replace(/^(?!<[h|li|ul|ol])(.*$)/gim, '<p class="mb-1">$1</p>');

    // Auto-link des URLs whitelistees présentes en texte brut (après la structuration de paragraphes)
    if (autolink) {
        html = autolinkWhitelisted(html);
    }

    // Nettoyer les paragraphes vides et wrapper les listes
    html = html
        .replace(/<p class="mb-1"><\/p>/g, "")
        .replace(/<li class="ml-1">(.*?)<\/li>/g, '<ul class="list-disc ml-4 mb-2"><li class="ml-1">$1</li></ul>')
        .replace(/<\/ul>\s*<ul class="list-disc ml-4 mb-1">/g, "");

    // Nettoyer les balises p qui entourent les listes
    html = html.replace(/<p class="mb-1"><ul class="list-disc ml-4 mb-1">/g, '<ul class="list-disc ml-4 mb-2">').replace(/<\/ul><\/p>/g, "</ul>");

    // Utiliser DOMPurify pour une sécurité supplémentaire
    // Configurer DOMPurify pour autoriser seulement les balises et attributs que nous utilisons
    const cleanHtml = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ["h1", "h2", "h3", "p", "strong", "em", "code", "a", "ul", "li", "img"],
        ALLOWED_ATTR: ["class", "href", "target", "rel", "src", "alt", "title", "loading", "decoding"],
        ALLOW_DATA_ATTR: false,
        FORBID_TAGS: ["script", "object", "embed", "form", "input"],
        FORBID_ATTR: ["onclick", "onload", "onerror", "onmouseover"]
    });

    return cleanHtml;
};

const REMARK_PLUGINS_DEFAULT = [remarkGfm];
const REMARK_PLUGINS_SOFT_BREAKS = [remarkGfm, remarkBreaks];

export function MarkdownRenderer({ content, softBreaks = false, className }: MarkdownRendererProps) {
    const remarkPlugins = softBreaks ? REMARK_PLUGINS_SOFT_BREAKS : REMARK_PLUGINS_DEFAULT;
    const components = useMemo<Components>(
        () => ({
            a({ href, children, ...rest }) {
                return (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800" {...rest}>
                        {children}
                    </a>
                );
            },
            code({ className: codeClassName, children, ...rest }) {
                const isInline = !codeClassName;
                if (isInline) {
                    return (
                        <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[12px]" {...rest}>
                            {children}
                        </code>
                    );
                }
                return (
                    <code className={codeClassName} {...rest}>
                        {children}
                    </code>
                );
            },
            pre({ children }) {
                return <pre className="my-2 overflow-x-auto rounded-md bg-gray-100 p-3 text-sm">{children}</pre>;
            },
            img({ src, alt, title }) {
                return <img src={src} alt={alt ?? ""} title={title} loading="lazy" decoding="async" className="max-w-full rounded-lg" />;
            },
            li({ className: liClassName, children, ...rest }) {
                if (liClassName?.includes("task-list-item")) {
                    return (
                        <li className="my-0.5 flex list-none items-start gap-2" style={{ listStyleType: "none" }}>
                            {children}
                        </li>
                    );
                }
                return (
                    <li className={liClassName} {...rest}>
                        {children}
                    </li>
                );
            },
            input({ type, checked }) {
                if (type === "checkbox") {
                    return (
                        <span
                            className={cn(
                                "mr-1.5 mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border",
                                checked ? "border-primary bg-primary" : "border-current/50 bg-transparent"
                            )}
                            aria-hidden="true"
                        >
                            {!!checked && (
                                <svg viewBox="0 0 10 10" className="h-2.5 w-2.5 text-primary-foreground" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M1.5 5l2.5 2.5 5-5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </span>
                    );
                }
                return <input type={type} />;
            }
        }),
        []
    );

    return (
        <div
            className={cn(
                "prose prose-sm max-w-none",
                // Titres
                "prose-headings:font-medium",
                "prose-h1:mb-4 prose-h1:text-2xl",
                "prose-h2:mb-3 prose-h2:mt-6 prose-h2:text-xl",
                "prose-h3:mb-2 prose-h3:mt-4 prose-h3:text-lg",
                // Paragraphes
                "prose-p:my-2 prose-p:leading-relaxed",
                // Listes
                "prose-ol:my-2 prose-ol:list-decimal prose-ol:pl-4",
                "prose-ul:my-2 prose-ul:list-disc prose-ul:pl-4",
                "prose-li:my-0.5",
                // Liens
                "prose-a:no-underline",
                // Blockquote
                "prose-blockquote:border-l-2 prose-blockquote:border-black/20 prose-blockquote:pl-4 prose-blockquote:italic",
                // Supprime les guillemets automatiques (« ») ajoutés par le plugin typography
                "[&_blockquote_p:first-of-type::before]:content-none [&_blockquote_p:last-of-type::after]:content-none",
                // Code
                "prose-code:font-normal prose-code:text-black prose-code:before:content-none prose-code:after:content-none",
                // Tableaux
                "prose-table:w-full prose-table:border-collapse",
                "prose-th:border prose-th:border-border prose-th:bg-black/5 prose-th:p-2",
                "prose-td:border prose-td:border-border prose-td:p-2",
                className
            )}
        >
            <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
                {content}
            </ReactMarkdown>
        </div>
    );
}

// Version alternative plus simple pour les descriptions courtes
export function SimpleMarkdownRenderer({ content, autolink = true, className }: MarkdownRendererProps) {
    const htmlContent = markdownToHtml(content, autolink);

    return <span className={className} dangerouslySetInnerHTML={{ __html: htmlContent }} />;
}
